import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Zap, ArrowLeft, CheckCircle2, Circle, Loader2, School, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ItemPhotoButton from "@/components/ItemPhotoButton";
import ServicePhotoForm from "@/components/ServicePhotoForm";
import { api } from "@/lib/api";

const fileSrc = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
const Mini = ({ path, label, testid }) => (
  <a href={fileSrc(path)} target="_blank" rel="noreferrer" className="relative w-12 h-12 rounded-md overflow-hidden border border-slate-200 shrink-0" data-testid={testid}>
    <img src={fileSrc(path)} alt={label} loading="lazy" className="w-full h-full object-cover" />
    <span className="absolute bottom-0 inset-x-0 text-[8px] font-black uppercase text-center bg-black/55 text-white leading-tight">{label}</span>
  </a>
);

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const clean = (s) => (s || "").replace(/\[\/?b\]/g, "");

const BudgetPicker = ({ list, onPick }) => {
  if (list === null) return <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!list.length) return <p className="py-6 text-center text-sm text-slate-500" data-testid="quick-exec-empty">Nenhum orçamento iniciado.</p>;
  return (
    <ul className="space-y-2" data-testid="quick-exec-budgets">
      {list.map((b) => (
        <li key={b.id}>
          <button type="button" onClick={() => onPick(b)} className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50/50 transition-colors" data-testid={`quick-exec-budget-${b.id}`}>
            <span className="w-9 h-9 rounded-lg bg-[#1e3a8a] text-white flex items-center justify-center shrink-0"><School className="w-4 h-4" /></span>
            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-bold text-slate-900 truncate">{b.school_name || "—"}</span>
              <span className="block text-[11px] text-slate-500">
                Orçamento Nº {b.number} · {b.items_completed ?? 0}/{b.items_count ?? 0} itens
                {b.nfa_open && <span className="ml-1 font-semibold text-indigo-700">· Nota gerada</span>}
              </span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
};

const ItemRow = ({ budgetId, it, idx, onToggle, onPhoto, busy }) => (
  <li className={`p-3 rounded-xl border ${it.completed ? "bg-emerald-50/60 border-emerald-200" : "bg-white border-slate-200"}`} data-testid={`quick-item-${it.id}`}>
    <div className="flex items-start gap-3">
      <button type="button" onClick={() => onToggle(it)} disabled={busy} className="shrink-0 mt-0.5" data-testid={`quick-item-check-${it.id}`} aria-label="Concluir item">
        {busy ? <Loader2 className="w-6 h-6 animate-spin text-slate-400" /> : it.completed ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <Circle className="w-6 h-6 text-slate-300" />}
      </button>
      <p className={`flex-1 min-w-0 text-[12.5px] leading-snug ${it.completed ? "text-slate-500" : "text-slate-800"}`}>
        <strong>{idx + 1}.</strong> {clean(it.description)}
      </p>
    </div>
    <div className="mt-2 pl-9 flex items-center gap-2 flex-wrap">
      {it.before_photo_path && <Mini path={it.before_photo_path} label="Antes" testid={`quick-thumb-before-${it.id}`} />}
      {it.after_photo_path && <Mini path={it.after_photo_path} label="Depois" testid={`quick-thumb-after-${it.id}`} />}
      <ItemPhotoButton budgetId={budgetId} item={it} onUpdate={(u) => onPhoto(it.id, u)} testid={`quick-photo-${it.id}`} />
    </div>
  </li>
);

export default function QuickExecDialog({ open, onOpenChange }) {
  const [list, setList] = useState(null);
  const [budget, setBudget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) { setBudget(null); return; }
    setList(null);
    Promise.all([api.get("/budgets?status=iniciando"), api.get("/budgets?nf_antecipada=true")])
      .then(([a, b]) => {
        const seen = new Set();
        const all = [...(b.data || []), ...(a.data || [])].filter((x) => !x.parent_budget_id && !seen.has(x.id) && seen.add(x.id));
        setList(all.sort((x, y) => (x.school_name || "").localeCompare(y.school_name || "")));
      })
      .catch(() => setList([]));
  }, [open]);

  const pick = async (b) => {
    setBudget({ ...b, items: null });
    try {
      const r = await api.get(`/budgets/${b.id}`);
      setBudget({ ...r.data, school_name: b.school_name });
    } catch {
      toast.error("Erro ao abrir orçamento");
      setBudget(null);
    }
  };

  const patchItem = (id, patch) => setBudget((p) => ({ ...p, items: p.items.map((x) => (x.id === id ? { ...x, ...patch } : x)) }));

  const toggle = async (it) => {
    setBusyId(it.id);
    try {
      const r = await api.post(`/budgets/${budget.id}/items/${it.id}/complete-toggle`, { completed_at: localToday() });
      if (r.offline || !r.data?.items) patchItem(it.id, { completed: !it.completed });
      else setBudget((p) => ({ ...r.data, school_name: p.school_name }));
      toast.success(it.completed ? "Item reaberto" : "Item concluído");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao concluir");
    } finally { setBusyId(null); }
  };

  const items = budget?.items;
  const done = (items || []).filter((x) => x.completed).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden w-[calc(100vw-1.5rem)] sm:w-full grid-cols-1 [&>*]:min-w-0" data-testid="quick-exec-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1e3a8a] text-base">
            {budget ? (
              <button type="button" onClick={() => setBudget(null)} className="w-7 h-7 rounded-md hover:bg-slate-100 flex items-center justify-center" data-testid="quick-exec-back" aria-label="Voltar"><ArrowLeft className="w-4 h-4" /></button>
            ) : <Zap className="w-5 h-5 text-amber-500" />}
            <span className="min-w-0 truncate">{budget ? budget.school_name || "" : "Execução rápida"}</span>
          </DialogTitle>
        </DialogHeader>
        {!budget ? (
          <BudgetPicker list={list} onPick={pick} />
        ) : !items ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-slate-600" data-testid="quick-exec-progress"><strong className="text-[#1e3a8a]">Nº {budget.number}</strong> · <strong>{done} de {items.length}</strong> itens concluídos</p>
              <Link to={`/orcamentos/${budget.id}`} onClick={() => onOpenChange(false)} className="text-[11px] font-semibold text-violet-700 inline-flex items-center gap-1 hover:underline" data-testid="quick-exec-open-budget">
                Abrir orçamento <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }} />
            </div>
            <ServicePhotoForm schoolId={budget.school_id} schoolName={budget.school_name} />
            <ul className="space-y-2">
              {[...items.map((it, i) => ({ it, i }))]
                .sort((a, b) => Number(!!a.it.completed) - Number(!!b.it.completed) || a.i - b.i)
                .map(({ it, i }) => (
                <ItemRow key={it.id} budgetId={budget.id} it={it} idx={i} onToggle={toggle} busy={busyId === it.id}
                  onPhoto={(id, u) => patchItem(id, u || {})} />
              ))}
            </ul>
            <Button variant="outline" className="w-full" onClick={() => setBudget(null)} data-testid="quick-exec-other">Escolher outro orçamento</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
